/* OrgModel — W15.2 : Interactive Organizational Graph.
 * Projection visuelle du Living Organizational Model (jamais une base parallèle).
 * Réseau neuronal : organisation au centre, hubs par type, membres déployables,
 * chaque nœud cliquable → OBJET / RELATIONS / RESPONSABLE / ÉVIDENCE / HISTORIQUE.
 * Perspectives ALL / DEAL / FM / GOVERNANCE = même cerveau, filtres différents.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ReactFlow, Background, Controls, Handle, Position, MarkerType } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import TemporalDiffPanel from "@/components/TemporalDiffPanel";
import {
  Building2, User, Users2, Workflow, ListChecks, Server, Box, FileText,
  AlertTriangle, ShieldCheck, ClipboardCheck, Flame, Handshake, BarChart3,
  Scale, ListTodo, Target, Radio, Loader2, X, Search, Network, Crown,
  Radar, MessageCircle, Send, Check, CalendarClock,
} from "lucide-react";

const TYPE_META = {
  organization: { color: "#EF9F27", icon: Crown, label: "Organisation" },
  person:       { color: "#E2E8F0", icon: User, label: "Personnes" },
  team:         { color: "#34D399", icon: Users2, label: "Équipes" },
  org_entity:   { color: "#378ADD", icon: Building2, label: "Écosystème" },
  process:      { color: "#EFD127", icon: Workflow, label: "Processus" },
  activity:     { color: "#A3E635", icon: ListChecks, label: "Activités" },
  system:       { color: "#22D3EE", icon: Server, label: "Systèmes" },
  asset:        { color: "#4A89DC", icon: Box, label: "Actifs" },
  document:     { color: "#94A3B8", icon: FileText, label: "Documents" },
  risk:         { color: "#E24B4A", icon: AlertTriangle, label: "Risques" },
  control:      { color: "#1D9E75", icon: ShieldCheck, label: "Contrôles" },
  obligation:   { color: "#F472B6", icon: ClipboardCheck, label: "Obligations" },
  incident:     { color: "#FB923C", icon: Flame, label: "Incidents" },
  deal:         { color: "#7F77DD", icon: Handshake, label: "Deals" },
  instrument:   { color: "#38BDF8", icon: BarChart3, label: "Instruments" },
  decision:     { color: "#10B981", icon: Scale, label: "Décisions" },
  action:       { color: "#FBBF24", icon: ListTodo, label: "Actions" },
  outcome:      { color: "#C084FC", icon: Target, label: "Outcomes" },
  signal:       { color: "#F87171", icon: Radio, label: "Signaux" },
};
const meta = (t) => TYPE_META[t] || { color: "#64748B", icon: Network, label: t };

const PERSPECTIVES = [
  { key: "all", label: "ALL" }, { key: "deal", label: "DEAL" },
  { key: "fm", label: "FINANCIAL MARKETS" }, { key: "governance", label: "GOVERNANCE" },
];
const STATES = [
  { key: "all", label: "Tout" }, { key: "declared", label: "Declared" },
  { key: "observed", label: "Observed" }, { key: "expected", label: "Expected" },
];
const STATE_DOT = { declared: "#34D399", observed: "#38BDF8", expected: "#F472B6" };

function RootNode({ data }) {
  return (
    <div className="relative flex flex-col items-center" data-testid="org-model-root-node">
      <Handle type="target" position={Position.Top} className="!opacity-0" />
      <Handle type="source" position={Position.Bottom} className="!opacity-0" />
      <div className="w-24 h-24 rounded-full flex items-center justify-center border-2"
        style={{ borderColor: "#EF9F27", background: "radial-gradient(circle, rgba(239,159,39,0.25) 0%, rgba(5,7,12,0.9) 70%)", boxShadow: "0 0 42px rgba(239,159,39,0.45)" }}>
        <Crown className="w-9 h-9" style={{ color: "#EF9F27" }} />
      </div>
      <div className="mt-2 text-[13px] font-semibold text-white text-center max-w-[160px]">{data.label}</div>
      <div className="text-[10px] text-slate-400">Living Organizational Model</div>
    </div>
  );
}

function HubNode({ data }) {
  const m = meta(data.htype);
  const Icon = m.icon;
  return (
    <div className="relative flex flex-col items-center cursor-pointer" data-testid={`hub-${data.htype}`}>
      <Handle type="target" position={Position.Top} className="!opacity-0" />
      <Handle type="source" position={Position.Bottom} className="!opacity-0" />
      <div className="w-16 h-16 rounded-full flex items-center justify-center border transition-transform hover:scale-110"
        style={{ borderColor: m.color, background: `radial-gradient(circle, ${m.color}26 0%, rgba(5,7,12,0.92) 72%)`, boxShadow: data.expanded ? `0 0 34px ${m.color}88` : `0 0 18px ${m.color}44` }}>
        <Icon className="w-6 h-6" style={{ color: m.color }} />
      </div>
      <div className="mt-1.5 text-[12px] font-medium text-white">{m.label}</div>
      <div className="text-[10px]" style={{ color: m.color }}>
        {data.expanded && data.count > 36 ? `36 / ${data.count} affichés` : `${data.count} ${data.expanded ? "· replier" : "· déployer"}`}
      </div>
    </div>
  );
}

function MemberNode({ data }) {
  const m = meta(data.ntype);
  return (
    <div className="relative flex items-center gap-1.5 cursor-pointer group" data-testid={`member-${data.nid}`}>
      <Handle type="target" position={Position.Left} className="!opacity-0" />
      <Handle type="source" position={Position.Right} className="!opacity-0" />
      <span className="w-3 h-3 rounded-full shrink-0 group-hover:scale-150 transition-transform"
        style={{ background: m.color, boxShadow: `0 0 10px ${m.color}` }} />
      <span className="text-[10px] text-slate-200 max-w-[130px] truncate">{data.label}</span>
      {data.unresolved && <span className="text-[9px] text-amber-400">?</span>}
    </div>
  );
}

const nodeTypes = { rootN: RootNode, hubN: HubNode, memberN: MemberNode };
const CX = 640, CY = 380;

const VIA_FR = { responsible_for: "responsable de", owns: "porte", approves: "approuve", accountable_for: "redevable de" };
const SOURCE_FR = {
  organisations: "Organisation (tenant)", users: "Comptes utilisateurs", workspace_annuaire_entities: "Annuaire",
  workspace_processes: "Registre des processus", workspace_documents: "Documents · Drive",
  workspace_gov_documents: "Corpus documentaire Governance", workspace_risks: "Registre des risques",
  workspace_controles: "Registre des contrôles", workspace_obligations: "Registre des obligations",
  workspace_incidents: "Registre des incidents", workspace_deals: "Deals", workspace_decisions: "Journal des décisions",
  workspace_plans: "Plans d'action", workspace_signals: "Moteur de signaux", workspace_re_assets: "Actifs immobiliers",
  workspace_brvm_companies: "Compagnies cotées suivies", workspace_trading_trades: "Transactions trading",
};

function LimitedList({ items, initial, render }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, initial);
  return (
    <>
      {shown.map(render)}
      {items.length > initial && (
        <button onClick={() => setAll((v) => !v)} data-testid="limited-list-toggle"
          className="text-[10px] text-sky-400 hover:underline px-2 py-0.5">
          {all ? "réduire" : `voir les ${items.length - initial} restants`}
        </button>
      )}
    </>
  );
}

const AXIS_STYLE = {
  "DECLARED↔EXPECTED": "text-emerald-300 border-emerald-400/30",
  "DECLARED↔OBSERVED": "text-sky-300 border-sky-400/30",
  "OBSERVED↔DECLARED": "text-sky-300 border-sky-400/30",
  "OBSERVED↔EXPECTED": "text-pink-300 border-pink-400/30",
  "EXPECTED↔OBSERVED": "text-pink-300 border-pink-400/30",
};
const AXIS_FR = {
  "DECLARED↔EXPECTED": "Déclaré ↔ Attendu", "DECLARED↔OBSERVED": "Déclaré ↔ Observé",
  "OBSERVED↔DECLARED": "Observé ↔ Déclaré", "OBSERVED↔EXPECTED": "Observé ↔ Attendu",
  "EXPECTED↔OBSERVED": "Attendu ↔ Observé",
};
const SEV_STYLE = { 3: "bg-red-500/15 text-red-400 border-red-500/40", 2: "bg-amber-500/15 text-amber-400 border-amber-500/40", 1: "bg-slate-500/15 text-slate-300 border-slate-500/40" };
const SEV_LABEL = { 3: "critique", 2: "important", 1: "à surveiller" };

function DivergencePanel({ divs, onClose, onOpenNode, activeDiv, setActiveDiv }) {
  return (
    <div className="absolute top-[92px] bottom-0 left-0 w-[380px] z-20 border-r border-t border-white/10 bg-[#080b12]/95 backdrop-blur-xl overflow-y-auto rounded-tr-lg"
      data-testid="divergence-panel">
      <div className="sticky top-0 bg-[#080b12] border-b border-white/10 px-4 py-3 flex items-center gap-2">
        <Radar className="w-4 h-4 text-red-400" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-white">Radar de divergences</div>
          <div className="text-[10px] text-slate-400">{divs?.total ?? 0} écarts · {divs?.objects_affected ?? 0} objets — Déclaré vs Observé vs Attendu</div>
        </div>
        <button onClick={onClose} data-testid="divergence-panel-close" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-3 space-y-2">
        {(divs?.items || []).map((it) => (
          <div key={it.id} data-testid={`divergence-item-${it.rule}`}
            onClick={() => setActiveDiv(activeDiv === it.rule ? null : it.rule)}
            className={`rounded-lg border p-3 cursor-pointer transition-colors ${activeDiv === it.rule ? "border-red-400/50 bg-red-500/5" : "border-white/10 hover:border-white/25"}`}>
            <div className="flex items-start gap-2">
              <span className={`text-[9px] px-1.5 py-0.5 rounded border shrink-0 ${SEV_STYLE[it.severity]}`} title={SEV_LABEL[it.severity]}>{SEV_LABEL[it.severity]}</span>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium text-white leading-tight">{it.title}</div>
                <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <span className={`text-[9px] px-1.5 rounded-full border ${AXIS_STYLE[it.axis] || "text-slate-400 border-white/15"}`}>{AXIS_FR[it.axis] || it.axis}</span>
                  <span className="text-[10px] text-slate-400">{it.count} objet{it.count > 1 ? "s" : ""}</span>
                  {it.owner && <span className="text-[9px] text-slate-500">→ {it.owner}</span>}
                </div>
              </div>
            </div>
            <p className="mt-1.5 text-[10px] text-slate-400 leading-relaxed">{it.insight}</p>
            {it.possible_action && <p className="mt-1 text-[10px] text-emerald-300/80">Action : {it.possible_action}</p>}
            {activeDiv === it.rule && (
              <div className="mt-2 flex flex-wrap gap-1">
                {it.affected.map((a) => (
                  <button key={a.id} onClick={(e) => { e.stopPropagation(); onOpenNode(a.id); }}
                    data-testid={`divergence-affected-${a.id}`} title={a.label}
                    className="text-[9px] px-1.5 py-0.5 rounded border border-white/15 text-slate-300 hover:border-white/40 max-w-[170px] truncate">
                    <span className="inline-block w-1.5 h-1.5 rounded-full mr-1" style={{ background: meta(a.type).color }} />
                    {a.label}
                  </button>
                ))}
                {it.count > it.affected.length && <span className="text-[9px] text-slate-500">… +{it.count - it.affected.length}</span>}
                <button onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const r = await api.post("/workspace/org-model/divergences/convene",
                      { rule: it.rule, object_id: it.affected[0].id });
                    notify.success(`Obligation créée pour ${r.data.owner}${r.data.idempotent ? " (déjà convoquée)" : ""}.`);
                  } catch (err) { notify.error(fmtError(err)); }
                }} data-testid={`divergence-convene-${it.rule}`}
                  className="text-[9px] px-2 py-0.5 rounded-full border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10">
                  Convoquer le responsable
                </button>
              </div>
            )}
          </div>
        ))}
        {divs && !divs.items?.length && <div className="text-[11px] text-slate-500 p-3">Aucune divergence détectée — les trois états concordent.</div>}
        <div className="text-[9px] text-slate-600 px-1 pb-2">{divs?.doctrine}</div>
      </div>
    </div>
  );
}

function ConversationPanel({ onClose, onGraphChanged }) {
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "Racontez-moi simplement comment votre entreprise fonctionne — équipes, responsables, processus, systèmes. Je proposerai les objets et relations, vous validez.", proposals: [] }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const r = await api.post("/workspace/org-model/conversation", { message: text, session_id: sessionId });
      setSessionId(r.data.session_id);
      setMsgs((m) => [...m, { role: "assistant", text: r.data.reply, questions: r.data.questions, proposals: r.data.proposals }]);
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };
  const curate = async (pid, action, msgIdx) => {
    try {
      const r = await api.post(`/workspace/org-model/proposals/${pid}/${action}`);
      setMsgs((m) => m.map((msg, i) => i !== msgIdx ? msg : {
        ...msg, proposals: msg.proposals.map((p) => p.id === pid ? { ...p, status: r.data.status } : p),
      }));
      if (action === "accept") { notify.success(`Intégré au modèle (${r.data.collection}).`); onGraphChanged(); }
    } catch (e) { notify.error(fmtError(e)); }
  };
  return (
    <div className="absolute top-[92px] bottom-0 left-0 w-[400px] z-20 border-r border-t border-white/10 bg-[#080b12]/95 backdrop-blur-xl flex flex-col rounded-tr-lg"
      data-testid="conversation-panel">
      <div className="border-b border-white/10 px-4 py-3 flex items-center gap-2 shrink-0">
        <MessageCircle className="w-4 h-4 text-[#EF9F27]" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-white">Racontez votre organisation</div>
          <div className="text-[10px] text-slate-400">NOAM propose — vous validez. Rien ne devient vérité sans vous.</div>
        </div>
        <button onClick={onClose} data-testid="conversation-panel-close" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {msgs.map((m, i) => (
          <div key={i}>
            <div className={`text-[11px] leading-relaxed rounded-lg px-3 py-2 ${m.role === "user" ? "bg-[#EF9F27]/10 text-slate-200 ml-8" : "bg-white/5 text-slate-300 mr-4"}`}>
              {m.text}
              {(m.questions || []).map((q, j) => <div key={j} className="mt-1 text-[10px] text-amber-300">? {q}</div>)}
            </div>
            {(m.proposals || []).map((p) => (
              <div key={p.id} className="mt-1.5 mr-4 rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-2" data-testid={`proposal-card-${p.id}`}>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: meta(p.payload.kind === "relation" ? "decision" : (p.payload.type === "organisation" ? "org_entity" : p.payload.type)).color }} />
                  <span className="text-[11px] text-slate-200 flex-1 truncate">
                    {p.payload.kind === "relation"
                      ? `${p.payload.from_label} — ${VIA_FR[p.payload.rel_type] || p.payload.rel_type} → ${p.payload.to_label}`
                      : `${p.payload.type} : ${p.payload.label}`}
                  </span>
                  <span className="text-[9px] text-slate-500 shrink-0">{Math.round((p.confidence || 0) * 100)}%</span>
                  {p.status === "proposed" ? (
                    <>
                      <button onClick={() => curate(p.id, "accept", i)} data-testid={`proposal-accept-${p.id}`}
                        className="p-1 rounded bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/30"><Check className="w-3 h-3" /></button>
                      <button onClick={() => curate(p.id, "reject", i)} data-testid={`proposal-reject-${p.id}`}
                        className="p-1 rounded bg-red-500/15 text-red-400 hover:bg-red-500/30"><X className="w-3 h-3" /></button>
                    </>
                  ) : (
                    <span className={`text-[9px] ${p.status === "accepted" ? "text-emerald-400" : "text-red-400"}`}>{p.status === "accepted" ? "intégré" : "rejeté"}</span>
                  )}
                </div>
                {p.excerpt && <div className="mt-1 text-[9px] text-slate-500 italic truncate">« {p.excerpt} »</div>}
              </div>
            ))}
          </div>
        ))}
        {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-500" />}
      </div>
      <div className="border-t border-white/10 p-3 flex gap-2 shrink-0">
        <input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="« Sophie valide les investissements supérieurs à 5 M… »"
          data-testid="conversation-input"
          className="flex-1 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-[11px] text-white outline-none placeholder:text-slate-500" />
        <button onClick={send} disabled={busy} data-testid="conversation-send"
          className="p-2 rounded-full bg-[#EF9F27] text-black disabled:opacity-40"><Send className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{title}</div>
      {children}
    </div>
  );
}

function RelRow({ r, onOpen, dir }) {
  const m = meta(r.other.type);
  return (
    <button onClick={() => onOpen(r.other.id)} data-testid={`rel-${r.other.id}`}
      className="w-full flex items-center gap-2 text-left px-2 py-1 rounded hover:bg-white/5">
      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: m.color }} />
      <span className="text-[11px] text-slate-300 truncate flex-1">{r.other.label}</span>
      <span className="text-[9px] text-slate-500 shrink-0">{dir === "in" ? "←" : "→"} {r.label}{r.inferred ? " · inféré" : ""} · {Math.round(r.confidence * 100)}%</span>
    </button>
  );
}

function DetailPanel({ nodeId, onClose, onOpen }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get(`/workspace/org-model/node?id=${encodeURIComponent(nodeId)}`)
      .then((r) => live && setD(r.data))
      .catch((e) => notify.error(fmtError(e)))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [nodeId]);
  const m = d ? meta(d.object.type) : null;
  return (
    <div className="absolute top-[92px] bottom-0 right-0 w-[360px] z-20 border-l border-t border-white/10 bg-[#080b12]/95 backdrop-blur-xl overflow-y-auto rounded-tl-lg"
      data-testid="org-model-detail-panel">
      <div className="sticky top-0 bg-[#080b12] border-b border-white/10 px-4 py-3 flex items-start gap-2">
        {m && <span className="mt-1 w-3 h-3 rounded-full shrink-0" style={{ background: m.color, boxShadow: `0 0 8px ${m.color}` }} />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white leading-tight">{d?.object?.label || "…"}</div>
          <div className="text-[10px] text-slate-400">{m?.label}{d?.object?.sublabel ? ` · ${d.object.sublabel}` : ""} · état {d?.object?.state}</div>
        </div>
        <button onClick={onClose} data-testid="org-model-detail-close" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      {/* Anti-flicker : le contenu précédent reste affiché pendant le chargement
          d'un autre nœud (spinner plein uniquement au tout premier chargement). */}
      {loading && !d ? (
        <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
      ) : d && (
        <div className={`p-4 transition-opacity ${loading ? "opacity-50" : "opacity-100"}`}>
          <Section title="Source de vérité">
            <div className="text-[11px] text-slate-300">{SOURCE_FR[d.object.source?.collection] || d.object.source?.collection || "—"}
              <span className="text-[9px] text-slate-500 font-mono ml-1">({d.object.source?.collection})</span>
            </div>
          </Section>
          <Section title="Responsable">
            {d.owner ? (
              <button onClick={() => onOpen(d.owner.id)} className="text-[11px] text-slate-200 hover:underline" data-testid="detail-owner">
                {d.owner.label}{d.owner.unresolved ? " · non résolu (UNKNOWN)" : ""} — {VIA_FR[d.owner.via] || d.owner.via} ({Math.round(d.owner.confidence * 100)}%)
              </button>
            ) : <div className="text-[11px] text-slate-500">Aucun responsable relié</div>}
          </Section>
          <Section title={`Relations entrantes (${d.relationships.in.length})`}>
            <LimitedList items={d.relationships.in} initial={12} render={(r, i) => <RelRow key={i} r={r} dir="in" onOpen={onOpen} />} />
            {!d.relationships.in.length && <div className="text-[11px] text-slate-500">—</div>}
          </Section>
          <Section title={`Relations sortantes (${d.relationships.out.length})`}>
            <LimitedList items={d.relationships.out} initial={12} render={(r, i) => <RelRow key={i} r={r} dir="out" onOpen={onOpen} />} />
            {!d.relationships.out.length && <div className="text-[11px] text-slate-500">—</div>}
          </Section>
          <Section title="Évidence / provenance">
            {d.evidence ? (
              <div className="text-[10px] text-slate-400 bg-white/5 rounded p-2 max-h-32 overflow-y-auto whitespace-pre-wrap" data-testid="detail-evidence">
                {typeof d.evidence === "string" ? d.evidence :
                  Object.entries(d.evidence).filter(([, v]) => v).slice(0, 8).map(([k, v]) => `${k} : ${String(v).slice(0, 120)}`).join("\n")}
              </div>
            ) : <div className="text-[11px] text-slate-500">Aucune évidence attachée</div>}
          </Section>
          <Section title="Historique">
            {(d.history || []).slice(0, 8).map((h, i) => (
              <div key={i} className="text-[10px] text-slate-400 py-0.5 border-b border-white/5">
                {String(h.at || h.set_at || h.date || "").slice(0, 16)} · {h.type || h.to || h.event || "événement"}{h.by ? ` · ${h.by}` : ""}
              </div>
            ))}
            {!(d.history || []).length && <div className="text-[11px] text-slate-500">—</div>}
          </Section>
          {[["Décisions", d.decisions], ["Risques", d.risks], ["Signaux", d.signals], ["Actions & obligations", d.actions]].map(([t, arr]) => (
            arr?.length ? (
              <Section key={t} title={`${t} (${arr.length})`}>
                <LimitedList items={arr} initial={6} render={(r, i) => <RelRow key={i} r={r} dir="in" onOpen={onOpen} />} />
              </Section>
            ) : null
          ))}
        </div>
      )}
    </div>
  );
}

export default function OrgModel() {
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [perspective, setPerspective] = useState(() => new URLSearchParams(window.location.search).get("perspective") || "all");
  const [state, setState] = useState("all");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [atDate, setAtDate] = useState("");
  const [divs, setDivs] = useState(null);
  const [showDivs, setShowDivs] = useState(false);
  const [showConv, setShowConv] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const [activeDiv, setActiveDiv] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/workspace/org-model/graph?perspective=${perspective}&state=${state}&q=${encodeURIComponent(q)}&at=${atDate}`);
      setGraph(r.data);
    } catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  }, [perspective, state, q, atDate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get(`/workspace/org-model/divergences?at=${atDate}`).then((r) => setDivs(r.data)).catch(() => {});
  }, [atDate]);
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 450);
    return () => clearTimeout(t);
  }, [qInput]);

  const { nodes, edges } = useMemo(() => {
    if (!graph) return { nodes: [], edges: [] };
    const byType = {};
    graph.nodes.forEach((n) => {
      if (n.id === graph.root) return;
      (byType[n.type] = byType[n.type] || []).push(n);
    });
    const types = Object.keys(byType).sort((a, b) => byType[b].length - byType[a].length);
    const rootNode = graph.nodes.find((n) => n.id === graph.root);
    const rf = [{ id: graph.root, type: "rootN", position: { x: CX, y: CY }, data: { label: rootNode?.label || "Organisation" } }];
    const pos = { [graph.root]: { x: CX, y: CY } };
    const hubId = (t) => `hub:${t}`;
    types.forEach((t, i) => {
      const ang = (2 * Math.PI * i) / types.length - Math.PI / 2;
      const x = CX + Math.cos(ang) * 520, y = CY + Math.sin(ang) * 300;
      pos[hubId(t)] = { x, y };
      rf.push({ id: hubId(t), type: "hubN", position: { x, y }, data: { htype: t, count: byType[t].length, expanded: expanded.has(t) } });
      if (expanded.has(t)) {
        const members = byType[t].slice(0, 36);
        members.forEach((n, j) => {
          const a2 = (2 * Math.PI * j) / members.length;
          const r2 = 120 + (members.length > 14 ? 55 : 0) + (j % 2) * 34;
          const mx = x + Math.cos(a2) * r2, my = y + Math.sin(a2) * r2;
          pos[n.id] = { x: mx, y: my };
          rf.push({ id: n.id, type: "memberN", position: { x: mx, y: my }, data: { nid: n.id, ntype: n.type, label: n.label, unresolved: n.meta?.unresolved } });
        });
      }
    });
    const visible = new Set(rf.map((n) => n.id));
    const resolve = (id) => {
      if (visible.has(id)) return id;
      const n = graph.nodes.find((x) => x.id === id);
      return n && visible.has(hubId(n.type)) ? hubId(n.type) : null;
    };
    const agg = {};
    graph.edges.forEach((e) => {
      const s = resolve(e.source), t = resolve(e.target);
      if (!s || !t || s === t) return;
      const key = `${s}|${t}`;
      if (!agg[key]) agg[key] = { source: s, target: t, count: 0, labels: {}, inferred: true, state: e.state, maxConf: 0 };
      const a = agg[key];
      a.count += 1;
      a.labels[e.label] = (a.labels[e.label] || 0) + 1;
      a.inferred = a.inferred && e.inferred;
      a.maxConf = Math.max(a.maxConf, e.confidence);
    });
    // Structure au repos, causalité à la sélection : le focus met en avant les
    // relations directes et atténue le reste du réseau.
    const focus = hovered || (selected && visible.has(selected) ? selected : null);
    const neighborhood = new Set();
    if (focus) {
      neighborhood.add(focus);
      Object.values(agg).forEach((a) => {
        if (a.source === focus) neighborhood.add(a.target);
        if (a.target === focus) neighborhood.add(a.source);
      });
    }
    const totalAgg = Object.keys(agg).length;
    rf.forEach((n) => {
      n.style = { ...(n.style || {}), opacity: focus && !neighborhood.has(n.id) ? 0.22 : 1, transition: "opacity 160ms" };
    });
    const rfEdges = Object.entries(agg).map(([key, a]) => {
      const domLabel = Object.entries(a.labels).sort((x, y) => y[1] - x[1])[0][0];
      const tgt = graph.nodes.find((x) => `hub:${x.type}` === a.target || x.id === a.target);
      const color = meta(tgt?.type || a.target.replace("hub:", "")).color;
      const inFocus = focus && (a.source === focus || a.target === focus);
      const dimmed = focus && !inFocus;
      const showLabel = inFocus || (!focus && totalAgg <= 42);
      return {
        id: key, source: a.source, target: a.target,
        type: "straight",
        label: showLabel ? (a.count > 1 ? `${domLabel} ×${a.count}` : domLabel) : undefined,
        animated: a.state === "observed" && !dimmed,
        style: {
          stroke: inFocus ? color : `${color}55`,
          strokeWidth: inFocus ? 1.8 : Math.min(0.8 + Math.log2(a.count + 1) * 0.4, 1.6),
          strokeDasharray: a.inferred ? "4 4" : undefined,
          opacity: dimmed ? 0.08 : 1,
          transition: "opacity 160ms, stroke 160ms",
        },
        labelStyle: { fill: inFocus ? "#d8e0ee" : "#8b98ad", fontSize: 9, fontWeight: inFocus ? 600 : 400 },
        labelBgStyle: { fill: "#05070c", fillOpacity: 0.8 },
        markerEnd: { type: MarkerType.ArrowClosed, color: inFocus ? color : `${color}77`, width: 9, height: 9 },
      };
    });
    return { nodes: rf, edges: rfEdges };
  }, [graph, expanded, hovered, selected]);

  const onNodeClick = useCallback((_, node) => {
    if (node.id.startsWith("hub:")) {
      const t = node.id.slice(4);
      setExpanded((prev) => { const n = new Set(prev); n.has(t) ? n.delete(t) : n.add(t); return n; });
    } else {
      setSelected(node.id);
    }
  }, []);

  const st = graph?.stats;
  return (
    <div className="relative h-[calc(100vh-64px)] bg-[#05070c] overflow-hidden" data-testid="org-model-page">
      <div className="absolute top-0 left-0 right-0 z-30 px-4 pt-3 pb-2 bg-gradient-to-b from-[#05070c] via-[#05070c]/85 to-transparent pointer-events-none">
        <div className="flex flex-wrap items-center gap-3 pointer-events-auto">
          <div>
            <h1 className="text-lg font-semibold text-white leading-tight">Organisation vivante</h1>
            <p className="text-[11px] text-slate-400">Projection du modèle canonique — jamais une base parallèle. Un lien = une relation réelle ou une inférence identifiée.</p>
          </div>
          <div className="flex items-center gap-1 ml-auto rounded-full border border-white/10 bg-white/5 p-0.5">
            {PERSPECTIVES.map((p) => (
              <button key={p.key} onClick={() => { setPerspective(p.key); setExpanded(new Set()); }}
                data-testid={`org-model-perspective-${p.key}`}
                className={`px-3 py-1 rounded-full text-[10px] font-medium tracking-wide transition-colors ${perspective === p.key ? "bg-[#EF9F27] text-black" : "text-slate-300 hover:text-white"}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2 pointer-events-auto">
          <button onClick={() => { setShowDivs((v) => !v); setShowConv(false); }}
            data-testid="org-model-divergence-toggle"
            className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] border transition-colors ${showDivs ? "border-red-400/60 text-red-300 bg-red-500/10" : "border-white/10 text-slate-300 hover:text-white"}`}>
            <Radar className="w-3 h-3" /> Divergences
            {divs?.total > 0 && <span className="px-1 rounded-full bg-red-500/25 text-red-300">{divs.total}</span>}
          </button>
          <button onClick={() => { setShowConv((v) => !v); setShowDivs(false); }}
            data-testid="org-model-conversation-toggle"
            className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] border transition-colors ${showConv ? "border-[#EF9F27]/60 text-[#EF9F27] bg-[#EF9F27]/10" : "border-white/10 text-slate-300 hover:text-white"}`}>
            <MessageCircle className="w-3 h-3" /> Raconter
          </button>
          <button onClick={() => { setShowDiff((v) => !v); setShowDivs(false); setShowConv(false); }}
            data-testid="org-model-diff-toggle"
            className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] border transition-colors ${showDiff ? "border-[#EF9F27]/60 text-[#EF9F27] bg-[#EF9F27]/10" : "border-white/10 text-slate-300 hover:text-white"}`}>
            <CalendarClock className="w-3 h-3" /> Diff temporel
          </button>
          <div className={`flex items-center gap-1 rounded-full border px-2 py-0.5 ${atDate ? "border-[#EF9F27]/50 bg-[#EF9F27]/10" : "border-white/10 bg-white/5"}`}>
            <CalendarClock className={`w-3 h-3 ${atDate ? "text-[#EF9F27]" : "text-slate-400"}`} />
            <input type="date" value={atDate} onChange={(e) => setAtDate(e.target.value)}
              data-testid="org-model-date"
              className="bg-transparent text-[10px] text-slate-200 outline-none w-[104px] [color-scheme:dark]" />
            {atDate && (
              <button onClick={() => setAtDate("")} data-testid="org-model-date-reset"
                className="text-[9px] text-[#EF9F27] hover:underline">aujourd'hui</button>
            )}
          </div>
          {atDate && <span className="text-[10px] text-[#EF9F27]" data-testid="org-model-temporal-chip">Vue au {atDate} — l'organisation telle qu'elle était</span>}
          <div className="flex items-center gap-1">
            {STATES.map((s) => (
              <button key={s.key} onClick={() => setState(s.key)} data-testid={`org-model-state-${s.key}`}
                className={`px-2.5 py-0.5 rounded-full text-[10px] border transition-colors ${state === s.key ? "border-white/40 text-white bg-white/10" : "border-white/10 text-slate-400 hover:text-white"}`}>
                {s.key !== "all" && <span className="inline-block w-1.5 h-1.5 rounded-full mr-1" style={{ background: STATE_DOT[s.key] }} />}
                {s.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1">
            <Search className="w-3 h-3 text-slate-400" />
            <input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Chercher un nœud… (filtre en direct)"
              data-testid="org-model-search" className="bg-transparent text-[11px] text-white outline-none w-44 placeholder:text-slate-500" />
          </div>
          {graph && st && (
            <div className="flex items-center gap-2 text-[10px] text-slate-400 ml-auto" data-testid="org-model-stats">
              <span>{graph.nodes.length} nœuds · {graph.edges.length} relations{graph.nodes.length !== st.nodes_total ? ` (sur ${st.nodes_total} · ${st.edges_total})` : ""}</span>
              <span>·</span>
              <span className="text-emerald-400">{graph.edges.filter((e) => !e.inferred).length} déclarées</span><span>·</span>
              <span className="text-sky-400">{graph.edges.filter((e) => e.inferred).length} inférées</span>
              {st.unresolved_owners > 0 && (<><span>·</span><span className="text-amber-400">{st.unresolved_owners} UNKNOWN</span></>)}
              <span>·</span><span>{st.build_ms} ms</span>
            </div>
          )}
        </div>
        <div className="mt-1 text-[9px] text-slate-500">
          Declared = déclaré par un registre validé · Observed = constaté (signaux, incidents, faits) · Expected = attendu (obligations, échéances) — trait pointillé = relation inférée (avec % de confiance), trait plein = relation déclarée
        </div>
      </div>
      {/* Anti-flicker : le graphe reste MONTÉ pendant les rechargements (loader
          plein uniquement au premier chargement, indicateur discret ensuite). */}
      {loading && !graph ? (
        <div className="h-full flex items-center justify-center">
          <Loader2 className="w-7 h-7 animate-spin text-[#EF9F27]" />
        </div>
      ) : (
        <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} onNodeClick={onNodeClick}
          onNodeMouseEnter={(_, n) => setHovered(n.id)} onNodeMouseLeave={() => setHovered(null)}
          fitView fitViewOptions={{ padding: 0.15 }} minZoom={0.2} maxZoom={2.2}
          proOptions={{ hideAttribution: true }} nodesDraggable nodesConnectable={false}
          data-testid="org-model-canvas">
          <Background color="#141a26" gap={34} size={1.2} />
          <Controls position="bottom-left" showInteractive={false}
            className="!bg-[#0b0f18] !border-white/10 [&>button]:!bg-transparent [&>button]:!border-white/10 [&>button]:!fill-slate-300" />
        </ReactFlow>
      )}
      {loading && graph && (
        <div className="absolute bottom-4 right-4 z-20 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-white/10 bg-[#0b0f18]/90 text-[10px] text-slate-400"
          data-testid="org-model-refreshing">
          <Loader2 className="w-3 h-3 animate-spin text-[#EF9F27]" /> Actualisation…
        </div>
      )}
      {showDivs && (
        <DivergencePanel divs={divs} onClose={() => setShowDivs(false)}
          onOpenNode={(id) => setSelected(id)} activeDiv={activeDiv} setActiveDiv={setActiveDiv} />
      )}
      {showConv && <ConversationPanel onClose={() => setShowConv(false)} onGraphChanged={load} />}
      {showDiff && <TemporalDiffPanel onClose={() => setShowDiff(false)} onOpenNode={(id) => setSelected(id)} />}
      {selected && <DetailPanel nodeId={selected} onClose={() => setSelected(null)} onOpen={(id) => setSelected(id)} />}
    </div>
  );
}
